import type { SettingsState } from "src/components/settings"
import type { NavSectionProps } from "src/components/nav-section"
import type { Theme, SxProps, CSSObject, Breakpoint } from "@mui/material/styles"

import { useMemo } from "react"
import { useAuthContext } from "@/src/auth/hooks"
import { isJobDetails } from "@/src/utils/storage-available"
import useIsAiProcessing from "@/src/hooks/useIsAiProcessing"
import { useAppConfigContext } from "@/src/config/useAppConfigContext"

import { Stack, Divider } from "@mui/material"
import { useTheme } from "@mui/material/styles"

import { useBoolean } from "src/hooks/use-boolean"

import { varAlpha, stylesMode } from "src/theme/styles"

import { bulletColor } from "src/components/nav-section"
import { useSettingsContext } from "src/components/settings"

import { Main } from "./main"
import { NavMobile } from "./nav-mobile"
import { layoutClasses } from "../classes"
import { NavVertical } from "./nav-vertical"
import { LayoutSection } from "../core/layout-section"
import { AccountDrawer } from "../components/account-drawer"
import { SettingsButton } from "../components/settings-button"
import {
    navData as dashboardNavData,
    navDataAiProcessing as dashboardNavDataAiProcessing,
} from "../config-nav-dashboard"

// ----------------------------------------------------------------------

export type DashboardLayoutProps = {
    sx?: SxProps<Theme>
    children: React.ReactNode
    data?: {
        nav?: NavSectionProps["data"]
    }
}

export function DashboardLayout({ sx, children, data }: DashboardLayoutProps) {
    const theme = useTheme()

    const mobileNavOpen = useBoolean()

    const settings = useSettingsContext()

    const { appConfig } = useAppConfigContext()

    const navColorVars = useNavColorVars(theme, settings)

    const layoutQuery: Breakpoint = "lg"

    const isNavMini = settings.navLayout === "mini"

    const isNavHorizontal = settings.navLayout === "horizontal"

    const isNavVertical = isNavMini || settings.navLayout === "vertical"

    const { user } = useAuthContext()

    const isAiProcessing = useIsAiProcessing()
    const currentNavData = useMemo(
        () => (isAiProcessing ? dashboardNavDataAiProcessing : dashboardNavData),
        [isAiProcessing]
    )

    /**
     * Using this entrypoint to check the user auth status and the route/permissions boundary across routes
     */
    const navData =
        data?.nav ??
        currentNavData.map((v) => ({
            ...v,
            items: v.items.filter((i) => {
                if (i.permissions) {
                    return i.permissions.every((j) => user?.permissions?.includes(j))
                }
                if (i.title === "Credits") {
                    return (
                        user?.highestRole === "SUPER_ADMIN" ||
                        user?.highestRole === "ADMIN" ||
                        user?.highestRole === "MANAGER" ||
                        user?.highestRole === "TEAM_LEAD"
                    )
                }
                if (i.title === "Generate OTP") {
                    return (
                        user?.highestRole === "SUPER_ADMIN" ||
                        user?.highestRole === "ADMIN" ||
                        user?.highestRole === "MANAGER" ||
                        user?.highestRole === "TEAM_LEAD"
                    )
                }
                if (appConfig?.disableAddCandidates) {
                    if (i.title === "History" || i.title === "Get Started") {
                        return false
                    }
                }
                return true
            }),
        }))

    return (
        <>
            <NavMobile
                data={navData}
                open={mobileNavOpen.value}
                onClose={mobileNavOpen.onFalse}
                cssVars={navColorVars.section}
                slots={{
                    bottomArea: (
                        <Stack direction="row" sx={{ px: 3.5, pb: 1, justifyContent: "center" }}>
                            <AccountDrawer data-slot="account" />
                            <Divider flexItem orientation="vertical" sx={{ ml: "14px", mr: "4px" }} />
                            <SettingsButton data-slot="settings" />
                        </Stack>
                    ),
                }}
            />

            <LayoutSection
                /** **************************************
                 * Header
                 *************************************** */
                headerSection={null}
                /** **************************************
                 * Sidebar
                 *************************************** */
                sidebarSection={
                    isNavHorizontal || isJobDetails() ? null : (
                        <NavVertical
                            data={navData}
                            isNavMini={isNavMini}
                            layoutQuery={layoutQuery}
                            cssVars={navColorVars.section}
                            onToggleNav={() =>
                                settings.onUpdateField(
                                    "navLayout",
                                    settings.navLayout === "vertical" ? "mini" : "vertical"
                                )
                            }
                            slots={{
                                bottomArea: (
                                    <Stack direction="row" sx={{ px: 3.5, pb: 1, justifyContent: "center" }}>
                                        <AccountDrawer data-slot="account" />
                                        <Divider flexItem orientation="vertical" sx={{ ml: "14px", mr: "4px" }} />
                                        <SettingsButton data-slot="settings" />
                                    </Stack>
                                ),
                                bottomAreaMini: (
                                    <Stack sx={{ p: 1, justifyContent: "center", alignItems: "center", mb: 2 }}>
                                        <SettingsButton data-slot="settings" />
                                        <Divider sx={{ margin: "6px 0 14px", width: "100%" }} />
                                        <AccountDrawer data-slot="account" />
                                    </Stack>
                                ),
                            }}
                        />
                    )
                }
                /** **************************************
                 * Footer
                 *************************************** */
                footerSection={null}
                /** **************************************
                 * Style
                 *************************************** */
                cssVars={{
                    ...navColorVars.layout,
                    "--layout-transition-easing": "linear",
                    "--layout-transition-duration": "120ms",
                    "--layout-nav-mini-width": "88px",
                    "--layout-nav-vertical-width": "300px",
                    "--layout-nav-horizontal-height": "64px",
                    "--layout-dashboard-content-pt": theme.spacing(1),
                    "--layout-dashboard-content-pb": theme.spacing(8),
                    "--layout-dashboard-content-px": theme.spacing(5),
                }}
                sx={{
                    [`& .${layoutClasses.hasSidebar}`]: {
                        [theme.breakpoints.up(layoutQuery)]: {
                            transition: theme.transitions.create(["padding-left"], {
                                easing: "var(--layout-transition-easing)",
                                duration: "var(--layout-transition-duration)",
                            }),
                            pl: isNavMini ? "var(--layout-nav-mini-width)" : "var(--layout-nav-vertical-width)",
                        },
                    },
                    ...sx,
                }}
            >
                <Main isNavHorizontal={isNavHorizontal} mobileNavOpen={mobileNavOpen.onTrue}>
                    {children}
                </Main>
            </LayoutSection>
        </>
    )
}

// ----------------------------------------------------------------------

function useNavColorVars(theme: Theme, settings: SettingsState): Record<"layout" | "section", CSSObject> {
    const {
        vars: { palette },
    } = theme

    return useMemo(() => {
        switch (settings.navColor) {
            case "integrate":
                return {
                    layout: {
                        "--layout-nav-bg": palette.background.default,
                        "--layout-nav-horizontal-bg": varAlpha(palette.background.defaultChannel, 0.8),
                        "--layout-nav-border-color": varAlpha(palette.grey["500Channel"], 0.12),
                        "--layout-nav-text-primary-color": palette.text.primary,
                        "--layout-nav-text-secondary-color": palette.text.secondary,
                        "--layout-nav-text-disabled-color": palette.text.disabled,
                        [stylesMode.dark]: {
                            "--layout-nav-border-color": varAlpha(palette.grey["500Channel"], 0.08),
                            "--layout-nav-horizontal-bg": varAlpha(palette.background.defaultChannel, 0.96),
                        },
                    },
                    section: {},
                }
            case "apparent":
                return {
                    layout: {
                        "--layout-nav-bg": palette.grey[900],
                        "--layout-nav-horizontal-bg": varAlpha(palette.grey["900Channel"], 0.96),
                        "--layout-nav-border-color": "transparent",
                        "--layout-nav-text-primary-color": palette.common.white,
                        "--layout-nav-text-secondary-color": palette.grey[500],
                        "--layout-nav-text-disabled-color": palette.grey[600],
                        [stylesMode.dark]: {
                            "--layout-nav-bg": palette.grey[800],
                            "--layout-nav-horizontal-bg": varAlpha(palette.grey["800Channel"], 0.8),
                        },
                    },
                    section: {
                        // caption
                        "--nav-item-caption-color": palette.grey[600],
                        // subheader
                        "--nav-subheader-color": palette.grey[600],
                        "--nav-subheader-hover-color": palette.common.white,
                        // item
                        "--nav-item-color": palette.grey[500],
                        "--nav-item-root-active-color": palette.primary.light,
                        "--nav-item-root-open-color": palette.common.white,
                        // bullet
                        "--nav-bullet-light-color": bulletColor.dark,
                        // sub
                        ...(settings.navLayout === "vertical" && {
                            "--nav-item-sub-active-color": palette.common.white,
                            "--nav-item-sub-open-color": palette.common.white,
                        }),
                    },
                }
            default:
                throw new Error(`Invalid color: ${settings.navColor}`)
        }
    }, [
        palette.background.default,
        palette.background.defaultChannel,
        palette.common.white,
        palette.grey,
        palette.primary.light,
        palette.text.disabled,
        palette.text.primary,
        palette.text.secondary,
        settings.navColor,
        settings.navLayout,
    ])
}
